import React, { useEffect, useRef, useState } from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";

import ActaModal from "@/components/ui/ActaModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function MailInterventionAssociationDialog({
  open,
  saving = false,
  onClose,
  onSearchInterventions,
  onAssociate,
}) {
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [selectedIntervention, setSelectedIntervention] = useState(null);
  const [loading, setLoading] = useState(false);
  const requestRef = useRef(0);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setItems([]);
      setSelectedIntervention(null);
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    const term = search.trim();

    if (
      !open
      || !onSearchInterventions
      || term.length < 2
      || selectedIntervention
    ) {
      setItems([]);
      setLoading(false);
      return undefined;
    }

    const requestId = ++requestRef.current;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const response = await onSearchInterventions(term);
        if (requestId !== requestRef.current) return;
        setItems(response?.items || []);
      } catch {
        if (requestId === requestRef.current) setItems([]);
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [open, onSearchInterventions, search, selectedIntervention]);

  if (!open) return null;

  function choose(intervention) {
    setSelectedIntervention(intervention);
    setSearch(intervention?.title || "");
    setItems([]);
  }

  return (
    <ActaModal
      open
      onClose={saving ? undefined : onClose}
      title="Associer à une intervention"
    >
      <div className="min-w-0 space-y-4 [overflow-wrap:anywhere]" data-testid="mail-intervention-association-dialog">
        <div>
          <label
            htmlFor="mail-intervention-association-search"
            className="mb-1.5 block text-xs font-black uppercase tracking-wide text-[var(--do-muted)]"
          >
            Intervention
          </label>
          <div className="relative">
            <MagnifyingGlass
              size={17}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--do-muted)]"
            />
            <Input
              id="mail-intervention-association-search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setSelectedIntervention(null);
              }}
              placeholder="Titre, type, statut, machine, série, client ou site"
              className="min-h-[44px] pl-10"
              autoComplete="off"
              autoFocus
            />
          </div>
        </div>

        <div className="max-h-72 overflow-y-auto rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)]">
          {loading ? (
            <div className="p-4 text-sm text-[var(--do-muted)]">Recherche…</div>
          ) : items.length > 0 ? (
            items.map((intervention) => (
              <button
                key={intervention.id}
                type="button"
                onClick={() => choose(intervention)}
                className="flex min-h-[64px] w-full flex-col border-b border-[var(--do-border)] px-4 py-3 text-left last:border-b-0 hover:bg-[var(--do-bg-2)]"
              >
                <span className="text-sm font-black text-[var(--do-ink)]">
                  {intervention.title || "Intervention"}
                </span>
                <span className="mt-1 text-xs text-[var(--do-muted)]">
                  {[
                    intervention.type,
                    intervention.status,
                    intervention.machineName,
                    intervention.serialNumber,
                    intervention.clientName,
                    intervention.siteName,
                  ].filter(Boolean).join(" · ")}
                </span>
              </button>
            ))
          ) : selectedIntervention ? (
            <div className="p-4">
              <div className="text-sm font-black text-[var(--do-ink)]">
                {selectedIntervention.title || "Intervention"}
              </div>
              <div className="mt-1 text-xs text-[var(--do-muted)]">
                {[
                  selectedIntervention.type,
                  selectedIntervention.status,
                  selectedIntervention.machineName,
                  selectedIntervention.serialNumber,
                  selectedIntervention.clientName,
                  selectedIntervention.siteName,
                ].filter(Boolean).join(" · ")}
              </div>
            </div>
          ) : search.trim().length >= 2 ? (
            <div className="p-4 text-sm text-[var(--do-muted)]">
              Aucune intervention accessible trouvée.
            </div>
          ) : (
            <div className="p-4 text-sm text-[var(--do-muted)]">
              Saisissez au moins deux caractères.
            </div>
          )}
        </div>

        <p className="text-xs text-[var(--do-muted)]">
          Seules les interventions visibles dans votre périmètre GMAO sont proposées.
        </p>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="actaSecondary" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button
            type="button"
            variant="actaPrimary"
            onClick={() => onAssociate(selectedIntervention)}
            disabled={!selectedIntervention || saving}
          >
            {saving ? "Association…" : "Associer"}
          </Button>
        </div>
      </div>
    </ActaModal>
  );
}
